import { isDecisiveCore } from './scoring';
import type { Alignment, Question } from './types';

/**
 * ===========================================================================
 * THE DELIGHT RULE
 * ===========================================================================
 *
 * One decision, in one place: how much noise an answer is allowed to make.
 *
 * The whole point of a celebration is that it is not guaranteed. If every
 * selection threw sparkles, sparkles would mean "you tapped something" rather
 * than "we just found something in common", and the moment that actually
 * matters would be worth nothing.
 *
 *  - `sparkle`  the two answers genuinely landed in the same place. A few tiny
 *               hearts and sparks drift up and fade. Roughly one question in
 *               three, if two people are well matched — rare enough to feel
 *               earned.
 *  - `glow`     the same direction, different words. A single warm pulse
 *               around the exchange. No particles.
 *  - `none`     a real difference, or one that simply does not matter. Calm
 *               and neutral: the answers still arrive with the same gentle
 *               motion as every other answer, and nothing marks them as wrong.
 *               No red, no shake, no cross.
 *
 * This is deliberately separate from `scoring.ts`. Scoring decides what is
 * true; this decides how loudly to say it. Changing one must never quietly
 * change the other.
 */

export type Celebration = 'sparkle' | 'glow' | 'none';

/** How many particles each celebration emits. Small numbers on purpose. */
export const PARTICLE_COUNTS = {
  /** Per aligned answer, mid-journey. */
  sparkle: 10,
  /** The result page, when the whole journey lined up. Richer, not a casino. */
  finale: 18,
} as const;

/**
 * Maps an alignment judgement to the response it has earned.
 *
 * `null` — an answer that could not be compared at all — is treated exactly
 * like a difference: silence. Never celebrate something you could not read.
 *
 * Pass the question and one more rule applies: a near-miss on children,
 * intention or commitment gets no glow. A warm pulse is the interface saying
 * "nice, that lines up", and "I could see it with the right person" is not
 * something to congratulate somebody for — it is the beginning of a
 * conversation, and it is shown as one.
 */
export function celebrationFor(
  alignment: Alignment | null | undefined,
  question?: Question,
): Celebration {
  switch (alignment) {
    case 'exact':
      return 'sparkle';
    case 'close':
      return question && isDecisiveCore(question) ? 'none' : 'glow';
    default:
      return 'none';
  }
}

/**
 * The line above a comparison. Conversational, and careful: it describes the
 * one answer just given and never forecasts the result. "Same page" is a fact
 * about this question; "we're compatible" would be a claim this product has
 * not earned yet and does not make until the end.
 *
 * `close` used to read "Close enough", which is a verdict rather than an
 * observation — and on children it was the site deciding, on James's behalf,
 * that a difference did not matter. "Similar, not identical" says the same
 * thing about a Saturday night and stops short of settling anything.
 */
export const ALIGNMENT_RIBBON: Record<Alignment, { label: string; emoji: string }> = {
  exact: { label: 'Same page', emoji: '✨' },
  close: { label: 'Similar, not identical', emoji: '\u{1F642}' },
  neutral: { label: 'Ooh, noted', emoji: '\u{1F4DD}' },
  different: { label: 'Interesting…', emoji: '\u{1F914}' },
};

/** What `close` says on the three questions where nearly is not the same. */
export const CORE_CLOSE_RIBBON = {
  label: 'Worth talking about',
  emoji: '\u{1F4AC}',
} as const;

/**
 * The ribbon for one exchange. `null` when the two answers could not be
 * compared, in which case nothing is claimed about them at all.
 */
export function ribbonFor(
  alignment: Alignment | null | undefined,
  question?: Question,
): { label: string; emoji: string } | null {
  if (!alignment) return null;
  if (alignment === 'close' && question && isDecisiveCore(question)) {
    return CORE_CLOSE_RIBBON;
  }
  return ALIGNMENT_RIBBON[alignment];
}
